import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {
  getLegalItemById,
  createLegalItem,
  updateLegalItemById,
  getNextLegalCode,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams, useLocation} from 'react-router-dom'
import {LegalDetail} from './core/_models'
import './LegalTinaDetailPage.scss'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import LegalDetailView from './components/LegalTinaDetailView'
import LegalTinaForm from './components/LegalTinaForm'
import {showSwal} from '../../../../ui-kit'

const confirmDialog = (title: string) => {
  return showSwal({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
  })
}

const LegalTinaDetailPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const [internalLoading, setInternalLoading] = useState(false)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {uniqueCode: paramUniqueCode} = useParams()
  const uniqueCode = paramUniqueCode ? paramUniqueCode.replace(/%20/g, ' ') : undefined
  const isDetailMode = Boolean(uniqueCode)
  const isAddMode = !uniqueCode
  const [isEditMode, setIsEditMode] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isEditDisabled, setIsEditDisabled] = useState(false)
  const [legalChildren, setLegalChildren] = useState<any[]>([])

  const [legalDetail, setLegalDetail] = useState<LegalDetail>({
    id: 0,
    unique_code: '',
    heading: null,
    description: null,
    level: null,
    divider: null,
    created_at: null,
    deleted_at: null,
    upcoming_flag: false,
    sub_issue: false,
    parent_id: null,
    recommended_keyword: null,
    legal_original_id: 0,
  })
  const [pendingLegal, setPendingLegal] = useState<LegalDetail>({
    id: 0,
    unique_code: '',
    heading: null,
    description: null,
    level: null,
    divider: null,
    created_at: null,
    deleted_at: null,
    upcoming_flag: false,
    sub_issue: false,
    parent_id: null,
    recommended_keyword: null,
    legal_original_id: 0,
  })

  const fetchLegalDetail = async () => {
    if (!uniqueCode) return
    try {
      setInternalLoading(true)

      const detailRes = await getLegalItemById(axiosPrivate, uniqueCode)
      const detailResdata = detailRes.data.data

      if (detailResdata.legalResult) {
        setLegalDetail(detailResdata.legalResult)
      }

      if (detailResdata.pendingLegal) {
        setPendingLegal(detailResdata.pendingLegal)
        setIsEditDisabled(true)
      }

      if (detailResdata.legalChildren) {
        setLegalChildren(detailResdata.legalChildren)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setInternalLoading(false)
    }
  }

  const onLegalChildDelete = (deletedId: number | string) => {
    setLegalChildren((prev) => prev.filter((item: any) => String(item.id) !== String(deletedId)))
  }

  useEffect(() => {
    if (isDetailMode) {
      fetchLegalDetail()
    }
  }, [uniqueCode])

  const location = useLocation()
  useEffect(() => {
    if (location.pathname.endsWith('/edit')) {
      setIsEditMode(true)
    } else {
      setIsEditMode(false)
    }
  }, [location.pathname, uniqueCode])

  // Form handlers
  const handleLegalChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setLegalDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleParentDropdownChange = (parent_id) => {
    setLegalDetail((prev) => ({...prev, parent_id}))

    if (!isAddMode) return

    getNextLegalCode(axiosPrivate, parent_id)
      .then((res) => {
        const {level, unique_code} = res.data.data
        setLegalDetail((prev) => ({...prev, level, unique_code}))
      })
      .catch((err) => {
        console.error(err)
        showSwal('Error', 'Failed to auto-fill level/unique code', 'error')
      })
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const {name, value} = e.target
    setLegalDetail((prev) => ({
      ...prev,
      [name]: value === '1' ? true : value === '0' ? false : value === '' ? null : value,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['unique_code', 'heading', 'level']

    const isValid = requiredFields.every((key) => {
      const val = (legalDetail as any)[key]
      if (typeof val === 'boolean') return true
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
  }, [legalDetail])

  const handleSubmit = () => {
    const body = {
      unique_code: legalDetail.unique_code,
      heading: legalDetail.heading,
      description: legalDetail.description,
      level: legalDetail.level,
      divider: legalDetail.divider ? true : false,
      upcoming_flag: legalDetail.upcoming_flag ? true : false,
      sub_issue: legalDetail.sub_issue ? true : false,
      parent_id: legalDetail.parent_id,
      recommended_keyword: legalDetail.recommended_keyword,
    }

    console.log(body)
    if (isEditMode) body['legal_original_id'] = body['id'] = legalDetail.id

    confirmDialog(
      `Are you sure you want to ${isEditMode ? 'update' : 'create'} this legal item?`
    ).then((result) => {
      if (!result.isConfirmed) return

      setInternalLoading(true)

      if (isEditMode) {
        updateLegalItemById(axiosPrivate, uniqueCode, body)
          .then((res) => {
            if (res.status === 200) {
              showSwal({
                title: 'Data updated',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                if (location.pathname.endsWith('/edit')) {
                  navigate(`/legal-tina/${encodeURIComponent(uniqueCode!)}`)
                } else {
                  window.location.reload()
                }
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to update data', 'error')
          })
          .finally(() => {
            setInternalLoading(false)
          })
      } else {
        createLegalItem(axiosPrivate, body)
          .then((res) => {
            if (res.status === 200) {
              showSwal({
                title: 'Data created',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                navigate('/legal-tina')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to create data', 'error')
          })
          .finally(() => {
            setInternalLoading(false)
          })
      }
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/legal-tina')
        } else {
          window.location.reload()
        }
      }
    })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Legal TINA',
            path: 'legal-tina',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add' : isEditMode ? 'Edit' : legalDetail.heading || 'Detail'}
      </PageTitle>
      <div className='LegalTinaDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode
                          ? `Detail ${legalDetail.heading || ''}`
                          : 'Add New Legal Item'}
                      </h3>
                    </div>
                    <div className='d-flex gap-2'>
                      {ability.can('update', subject) && isDetailMode && !isEditMode ? (
                        pendingLegal.heading ? (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              isEditDisabled ? (
                                <Tooltip id='edit-disabled-tooltip'>
                                  Complete pending reviews for legal TINA to enable export data.
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className='d-inline-block'>
                              <Button
                                className='btn btn-primary d-flex gap-4 align-items-center'
                                onClick={() => setIsEditMode(true)}
                                disabled={internalLoading || isEditDisabled}
                              >
                                <span className='fa fa-pencil'></span>
                                Edit
                              </Button>
                            </span>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            className='btn btn-primary d-flex gap-4 align-items-center'
                            onClick={() => setIsEditMode(true)}
                            disabled={internalLoading}
                          >
                            <span className='fa fa-pencil'></span>
                            Edit
                          </Button>
                        )
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!internalLoading ? (
                  <>
                    {(ability.can('update', subject) || ability.can('create', subject)) &&
                    (isAddMode || isEditMode) ? (
                      <LegalTinaForm
                        legalDetail={legalDetail}
                        handleLegalChange={handleLegalChange}
                        handleSelectChange={handleSelectChange}
                        handleCancel={handleCancel}
                        handleSubmit={handleSubmit}
                        internalLoading={internalLoading}
                        isFormValid={isFormValid}
                        handleParentDropdownChange={handleParentDropdownChange}
                      />
                    ) : (
                      <LegalDetailView
                        isLoading={internalLoading}
                        legalDetail={legalDetail}
                        legalChildren={legalChildren}
                        onLegalChildDelete={onLegalChildDelete}
                      />
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default LegalTinaDetailPage
